/**
 * breakprimitive.ts — draw BOS / CHoCH as BOUNDED dashed segments with a label.
 *
 * THE SAME RULE AS EVERY OTHER FILE HERE: this computes no ICT rule. It receives prices,
 * timestamps, colours and text from `overlays.ts` (which received the geometry from the
 * Python API) and turns them into pixels. The only arithmetic below is coordinate
 * conversion and label placement.
 *
 * WHY A PRIMITIVE, AND WHY BOUNDED
 * --------------------------------
 * Andrei supplied a reference drawing (2026-07-27) and the grammar in it is specific: a
 * break is a SHORT dashed horizontal segment at the pierced swing's price, starting at
 * the swing that SET the level and ending just past the bar that broke it, with the name
 * (`BOS` / `CHoCH`) sitting above the line near the break. Nothing spans the pane.
 *
 * `series.createPriceLine` — what the retired MSS overlay used for its levels — cannot
 * express that. It draws the FULL pane width, always, so it makes a false claim twice
 * over: the level did not exist before the swing that set it, and it stopped being the
 * live level once it was broken. It also renders its `title` only in the price-axis
 * label, which is switched off here on purpose (a dozen stacked axis chips cover recent
 * price action), so those lines carried no identity at all — the same defect
 * `levelprimitive.ts` was written to fix for account levels.
 *
 * Segments are BOUNDED at both ends, which is also what makes drawing every break
 * readable: measured on the cached series the median break spans 7 bars (max 40), and
 * there are ~30 breaks per 300 bars. The retired overlay had to cap itself at the 8 most
 * recent levels precisely because full-width lines turned the pane into a grid; a bounded
 * segment does not accumulate, so nothing is hidden from the chart any more.
 *
 * `drawBackground` vs `draw`: segments and labels are foreground (they are the subject,
 * not a wash), unlike the OTE band in `rangeprimitive.ts`.
 */
import type {
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  ISeriesPrimitivePaneRenderer,
  ISeriesPrimitivePaneView,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';

/**
 * The fancy-canvas rendering scope, structurally re-declared.
 *
 * fancy-canvas is lightweight-charts' own rendering dependency, not a direct dependency
 * of this app, so its types are described here rather than imported from a package the
 * manifest does not list. Same reasoning as `rangeprimitive.ts`.
 */
type Scope = {
  context: CanvasRenderingContext2D;
  horizontalPixelRatio: number;
  verticalPixelRatio: number;
  bitmapSize: { width: number; height: number };
};
type Target = { useBitmapCoordinateSpace: (f: (scope: Scope) => void) => void };

/**
 * One break, as geometry. Every field arrives from the API payload except the
 * presentation ones (`color`, `label`, `emphasis`).
 */
export type BreakSegment = {
  /** The pierced swing's price — the height of the segment. */
  price: number;
  /** Bar that SET the level. The segment starts here, never before. */
  fromTime: Time;
  /** Bar whose swing PIERCED it. The segment ends just past here. */
  toTime: Time;
  color: string;
  /** 'BOS' | 'CHoCH' — drawn above the line, at the break end. */
  label: string;
  /** CHoCH: thicker line and a bolder plate. A character change outranks a
   *  continuation visually because it is the one that carries information. */
  emphasis: boolean;
};

/** Pixels the segment overshoots its break bar by, so the label sits clear of the
 *  candle that broke the level (the reference drawing overshoots too). Presentation
 *  only — it changes no price and no bar. */
const OVERSHOOT_PX = 26;

/**
 * One vertex of the swing zigzag. Degree and settled-ness are VERTEX facts, per the
 * ZIGZAG DECISION block in `overlays.ts` — the line itself spends exactly one dimension
 * (the open leg's dash) and carries nothing else.
 */
export type ZigzagVertex = {
  time: Time;
  price: number;
  /** Degree 2. Larger radius; see `ZZ_R_MAJOR`. */
  major: boolean;
  /** Printed but not settled → HOLLOW (stroke, not fill). */
  pending: boolean;
};

export type Zigzag = {
  /** In ascending time order. The polyline connects them in this order. */
  points: ZigzagVertex[];
  /** Dash the FINAL segment only — the open leg (last pivot → running extreme). */
  dashLast: boolean;
  /**
   * Which extreme the open leg is tracking, or `null` when there is no open leg.
   *
   * ON THE LINE RATHER THAN ON THE VERTEX, because it is a property of the leg, and because
   * `ZigzagVertex` deliberately has exactly four fields (`rightedge.test.ts` asserts the
   * key set — the zigzag has no text channel at all and must not grow one).
   *
   * It exists so `zigzagLiveTip` can extend the last vertex with the LIVE candle without
   * re-deriving the side from a price comparison: the running extreme can equal the pivot
   * that started the leg, where `min` and `max` disagree and one of them is a level no bar
   * printed.
   */
  liveKind: 'high' | 'low' | null;
  color: string;
  /** CSS px, scaled by the bitmap ratio at paint time. */
  width: number;
};

/** Vertex radii in CSS px, from the decision: major is 2x the radius (4x the area), well
 *  clear of the marker layer's 1.26 quantisation floor that made size-by-degree a dead
 *  channel there. */
const ZZ_R_MINOR = 2;
const ZZ_R_MAJOR = 4;

/** One vertex mark's placement problem, in already-converted BITMAP px. */
export interface VertexMark {
  x: number;
  y: number;
  /** Already scaled by the bitmap ratio. */
  r: number;
  /** Degree 2. Always drawn — see `planVertexMarks`. */
  major: boolean;
  /** Hollow. Always drawn — see `planVertexMarks`. */
  pending: boolean;
}

/**
 * Decide WHICH vertex marks have room. Pure: pixels in, indices out, no canvas — the same
 * split as `planLabels`, and for the same reason (this is behaviour, and behaviour inside a
 * canvas callback is unreachable from any test).
 *
 * WHY THIS EXISTS. Degree lives on vertex SIZE (fact 1 of the ZIGZAG DECISION block), and a
 * size channel needs the mark's own SILHOUETTE. Measured off the DRAWN coordinates at the
 * panes' opening zoom (redteam `t_1bc8ba85` D2; re-measured by
 * `process/reviews/t_4a1010f9/measure_thinning.py` against the same `geom.json`): a major's
 * disc is 8 CSS px across, and on 15m the median nearest mark was **4.32 CSS px** away, so
 * **29 of 36** majors had another mark's centre INSIDE their own footprint and only **6 of
 * 36** kept a clean outline. On 1W/1D the channel separates perfectly (6/6 and 4/4 of the
 * majors on screen, zero overlap, median nearest 12.19 / 22.69 CSS px).
 *
 * THE RULE, and every clause of it is forced by what the mark CARRIES:
 *   - a MAJOR mark is always drawn. Degree is the fact the size channel exists for, so
 *     thinning majors would delete the information instead of the noise.
 *   - a HOLLOW (pending) mark is always drawn. It is the sole carrier of fact 2 and of the
 *     open leg's live tip; a dropped one would read as "settled", the failure direction that
 *     misleads.
 *   - a settled MINOR mark is drawn only where its disc TOUCHES NO already-drawn disc.
 * Protected marks are considered FIRST, so a minor can never displace a major.
 *
 * SETTLED MINORS ARE THEN CONSIDERED IN THE ARRAY'S ORDER, AND THAT ORDER IS PART OF THE
 * ANSWER -- stated rather than glossed, because the first draft of this block claimed the
 * outcome was order-independent and the witness refuted it in one run. A greedy pass over a
 * chain of touching marks keeps the ones it reaches first: `[x=0, x=1, x=40]` keeps 0 and 40,
 * the same three reversed keeps 40 and 1. Order-independence would need a global optimum over
 * an interval graph, which is a far larger thing than this defect asks for, and it would still
 * have to break ties somehow. What matters is that the order is MEANINGFUL and FIXED: the
 * caller is `_drawZigzag`, whose array is the payload's own time order (`swingZigzag` sorts
 * ascending), so "the earlier of two colliding minors wins" is a rule a reader can hold, and
 * the same chart always thins the same way.
 *
 * NO PAD, AND THAT IS MEASURED RATHER THAN CHOSEN. Sweeping a clearance pad of 1/2/4 CSS px
 * (`process/reviews/t_4a1010f9/sweep_pad_d2.py`) moves the D2 table not at all — bare
 * touching already reaches the floor at 30 of 36 majors clean, and the 6 that remain are
 * overlapped by ANOTHER MAJOR, which no minor-thinning rule can reach and which misleads
 * nobody (the reader still sees a large disc, so degree still reads). A pad only costs
 * marks: on 15m it drops the settled minors kept from 107 to 89/67/47. A threshold with no
 * measurable benefit is a tuning knob this repo would have to keep calibrating.
 *
 * IT IS A COLLISION TEST, NOT A TIMEFRAME RULE. There is no `tf` parameter and no density
 * threshold: the same question is asked on every pane and on 1W/1D it suppresses NOTHING
 * (measured 0 of 40 and 0 of 27 marks on screen), so the panes where the channel works are
 * byte-identical. Zoom in on 15m and the marks come back, because the question is "is there
 * room", not "is this timeframe crowded".
 *
 * AND IT WITHHOLDS ONLY MARKS, NEVER GEOMETRY. The polyline is drawn in full — every vertex
 * is still joined at its own price, exactly as `planLabels` withholds a redundant word and
 * never a segment. A suppressed minor's position is still on the chart as the line's corner.
 */
export function planVertexMarks(marks: readonly VertexMark[]): number[] {
  const protectedIdx: number[] = [];
  const optional: number[] = [];
  marks.forEach((m, i) => (m.major || m.pending ? protectedIdx : optional).push(i));
  const drawn = [...protectedIdx];
  for (const i of optional) {
    const m = marks[i];
    const clear = drawn.every((j) => {
      const o = marks[j];
      return Math.hypot(m.x - o.x, m.y - o.y) >= m.r + o.r;
    });
    if (clear) drawn.push(i);
  }
  // Paint order follows the input order so the canvas walk matches the vertex list;
  // selection above is unchanged by this sort.
  return drawn.sort((a, b) => a - b);
}

/**
 * The zigzag tail's dash, in CSS px: 5 ink + 4 gap, a 9px period. The break segments above
 * carry their own copy of this literal and keep it whatever this function does — nothing
 * below may reach them, because a break segment's length is a property of the BREAK (median
 * 7 bars, max 40) and has never been measured short.
 */
const ZZ_DASH_INK = 5;
const ZZ_DASH_GAP = 4;

/**
 * One device pixel — the smallest mark a screen can draw crisply, and therefore the floor on
 * BOTH components of the dash rather than on the period.
 *
 * ON THE COMPONENT AND NOT THE PERIOD, which is measured rather than tidy. A period floor of 2
 * bitmap px looks sufficient and is not: the 5:4 split then gives the GAP `2 * 4/9 = 0.89`
 * device px, i.e. sub-pixel — the antialiased smear this floor exists to prevent, in the one
 * component that carries the interruption. So the binding quantity is the SMALLER component
 * (`ZZ_DASH_GAP`), and `ZZ_DASH_MIN_PERIOD_BITMAP` below is derived from it rather than chosen.
 */
const ZZ_DASH_MIN_COMPONENT_BITMAP = 1;

/**
 * The shortest dash period that keeps both components at one device pixel, in BITMAP px:
 * `9 / 4 = 2.25`. DERIVED, so a change to the 5:4 ratio cannot silently push the gap sub-pixel.
 *
 * BITMAP AND NOT CSS, and that unit is load-bearing. What smears is sub-DEVICE-pixel ink, so the
 * floor belongs in the unit the device draws in: on a retina pane (hr=2) that is 1.125 CSS px,
 * and expressing the floor in CSS px would refuse a period the screen can draw perfectly well.
 * Measured consequence on the shortest real geometry — a 1-bar 1W leg is 2.67 CSS px at the
 * rendered 418px pane width: at hr=2 the floor does not bind and it gets the full 2 periods.
 */
const ZZ_DASH_MIN_PERIOD_BITMAP =
  (ZZ_DASH_MIN_COMPONENT_BITMAP * (ZZ_DASH_INK + ZZ_DASH_GAP)) / Math.min(ZZ_DASH_INK, ZZ_DASH_GAP);

/** Periods a segment must show before the dash reads AS a dash rather than as a stub. */
const ZZ_DASH_MIN_PERIODS = 2;

/**
 * The open leg's dash, SCALED TO THE SEGMENT THAT CARRIES IT. Pure: CSS px in, bitmap px out.
 *
 * WHY THIS IS NOT A FIXED LITERAL ANY MORE (2026-08-23, card `t_fce34e7f`, from redteam
 * `t_1bc8ba85` D1). The dash is the ONE line-level dimension the ZIGZAG DECISION spends, and
 * it spends it on fact 4 — the fact the feature was created for. Measured on the RENDERED
 * canvas at each pane's own opening zoom (ZOOM ZERO, not a zoomed-in reader), the open leg's
 * segment was shorter than one 9px period on **28 of 57** 1W series, 3 of 41 1D and 6 of 17
 * 15m, median 1.48 periods on 1W. A segment under one period cannot show an interior gap BY
 * CONSTRUCTION: it renders as a short solid stub, indistinguishable from the settled legs
 * behind it, so on half the 1W panes the LINE half of fact 4 was not perceivable at all.
 * That is a property of the PANE GEOMETRY (1W puts ~3 years in ~420 CSS px, so a 4-bar leg is
 * ~10.7 px and a 1-bar leg is 2.67 px), not of anything the reader did.
 *
 * WHY SCALING RATHER THAN A SHORTER LITERAL. A shorter fixed period fails on its own terms:
 * the shortest legs measured 0.30 of a 9px period, i.e. **2.7 CSS px**, so even `[2, 2]` (a
 * 4px period, the card's own candidate) is still longer than the whole segment and still draws
 * a stub. No constant works, because the quantity the constant has to beat is the SEGMENT's
 * length and that varies by three orders of magnitude across the panes. Scaling is the same one
 * dimension, parameterised by the thing it has to fit inside.
 *
 * AND THE "ONE SHARED LITERAL" ARGUMENT AGAINST IT IS FALSE AT THE PRIMARY SOURCE. The
 * decision block claims the tail and `openLegDrawing`'s ray read as one object BECAUSE they
 * reuse one literal. They never did: the ray is drawn by `RangePrimitive._drawRays`, whose
 * `r.dashed` branch is `ctx.setLineDash([4 * hr, 4 * hr])` — an 8px period — against this
 * file's 9px. So the two dashes have differed by a pixel since the tail shipped, and what
 * makes them read as one object is the SHARED PRICE, the shared hollow tip and the shared
 * dashed-ness, not a literal that was never shared. Reading the code rather than the comment
 * is what freed this choice (.hermes.md §0b.1).
 *
 * THE RULE, in one line each:
 *   - a segment with room for >= 2 full periods at the base 9px keeps the base 9px EXACTLY,
 *     so a LONG segment is byte-identical — and that is a claim about segments, NOT about
 *     panes. Measured after the fix (rows in the evidence dir): 1D 20 of 20 series keep the
 *     base period (median 7.324 periods, min 2.270), but 15m keeps it on only 9 of 13 —
 *     AAVE 1.134, AERO 1.186, ACE 1.033 and ANIME 0.989 periods were UNDER two and are
 *     re-encoded, correctly, because that is exactly what this function is for. Any pane can
 *     hold a short leg; "1D/15m are untouched" would be false (redteam `t_fce34e7f` F1);
 *   - a shorter one gets `length / 2`, so it shows exactly 2 periods: ink, gap, ink, gap;
 *   - never below 1 device pixel of ink or of gap, because sub-pixel ink is not a dash.
 * The ink:gap ratio is held at the base 5:4 through the whole range, so a shrunken dash is
 * the same dash smaller and not a second pattern.
 *
 * ITS OWN DOCUMENTED LIMIT, stated rather than glossed, and it is RATIO-DEPENDENT because the
 * floor is: below `2 * ZZ_DASH_MIN_PERIOD_BITMAP / hr` CSS px of segment two periods are
 * unreachable — 4.5 CSS px at hr=1, 2.25 at hr=2, 1.5 at hr=3. Measured against every 1W open
 * leg the app offers, the shortest real segment is a 1-bar leg at 2.67 CSS px, which clears the
 * retina floor with two full periods and falls short of it on a non-retina pane, where it gets
 * 1.19 periods — still a visible interruption (ink, gap, ink) rather than the solid stub, and
 * strictly better than the 0.30 periods the fixed literal gave it. The residue is not repaired
 * by a smaller floor, which would trade a stub for a smear. Numbers in
 * `docs/reports/evidence/zigzag_dash_2026-08-23/`.
 */
export function zigzagDash(lengthCssPx: number, hr: number): [number, number] {
  const base = ZZ_DASH_INK + ZZ_DASH_GAP;
  // A non-finite length (an off-scale coordinate reached this) falls back to the base rather
  // than propagating NaN into `setLineDash`, which silently draws SOLID.
  const len = Number.isFinite(lengthCssPx) ? Math.max(0, lengthCssPx) : Infinity;
  // The floor is a DEVICE-pixel quantity converted into this function's CSS unit, so a retina
  // pane is not refused a period it can draw crisply. `hr` is a ratio and can be 0 or absent in
  // a degenerate scope; guard it rather than dividing by zero into Infinity.
  const floor = ZZ_DASH_MIN_PERIOD_BITMAP / (hr > 0 ? hr : 1);
  const period = Math.max(floor, Math.min(base, len / ZZ_DASH_MIN_PERIODS));
  const scale = period / base;
  return [ZZ_DASH_INK * scale * hr, ZZ_DASH_GAP * scale * hr];
}

/** Vertical tolerance for "these two labels are on the same row", in CSS px. A plate is
 *  13px tall, so two within 14px of each other overlap. Not a tuning knob — it is the
 *  plate's own height, read off the same constant that draws it. */
const ROW_PX = 14;

/** One label's placement problem, in already-converted pixel space. */
export interface LabelBox {
  /** Right edge of the segment — the plate's right edge sits here. */
  x1: number;
  /** The segment's y. */
  y: number;
  /** Measured plate width (text + padding). */
  w: number;
  /** Plate height. */
  h: number;
  /** CHoCH. Only used to keep the two kinds' shares balanced. */
  emphasis: boolean;
}

export interface LabelPlan {
  /** Indices into the input array, in paint order. */
  draw: number[];
  /** How many had no room. */
  hidden: number;
  /** Drawn count PER KIND — a blended total is what hid the starvation bug. */
  perKind: { BOS: number; CHoCH: number };
}

/** The two kinds, as the key of a per-kind record. Local to the placement pass. */
type BreakKindKey = 'BOS' | 'CHoCH';

/**
 * Decide WHICH labels have room. Pure: pixels in, indices out, no canvas.
 *
 * WHY THIS IS A PURE FUNCTION AND NOT A LOOP INSIDE `_draw`. THREE separate defects lived
 * in this decision, and every one of them shipped green through tsc + vite because they are
 * behaviour, not types. Frontend behaviour belongs in a pure function (this repo's rule
 * since the ticket-state incident) precisely so vitest can reach it.
 *
 * DEFECT 1 — no collision test at all. On the live 2,170-bar BTC/1D feed in an 896px pane
 * (0.41 px per bar) the labels overlapped 192 of 208 times: 92% unreadable, fragments like
 * `CHoCHoCH`, some drawn at NEGATIVE x. The reference drawing's grammar assumes ~300 bars.
 *
 * DEFECT 2 — priority ordering starves a kind. Giving CHoCH first refusal (it is the break
 * that carries information about a turn) measured **BOS 0, CHoCH 21** on that feed: all 124
 * CHoCHs consumed every row before a single one of the 101 BOSs was tried, so 45% of all
 * events had no text anywhere — and a blended "21 drawn" looked like ordinary thinning.
 * Plain left-to-right merely inverts it (**BOS 23, CHoCH 5**).
 *
 * DEFECT 3 — INTERLEAVING THE CANDIDATE ORDER IS NOT ENOUGH, and this one only surfaced
 * because the test for defect 2 failed on a synthetic fixture. Alternating the ORDER does
 * not bound the OUTCOME: the collision test rejects most candidates, so whichever kind
 * happens to align with the surviving row spacing takes everything. Measured on two shapes,
 * with interleaved order: uniformly alternating kinds gave **BOS 0 / CHoCH 6** (a stride
 * resonance — every placeable slot landed on the same parity), and kinds clustered in runs
 * with their real differing plate widths (37px vs 22px) gave **BOS 8 / CHoCH 1**.
 *
 * So the fairness has to live in the ACCEPTANCE, not the order: a rejected label does not
 * consume its turn. The pass keeps a per-kind cursor and only advances the OTHER kind's
 * turn when one is actually placed, so a kind that keeps colliding cannot spend the other's
 * slots. Both kinds still walk left to right, and the whole thing stays deterministic with
 * no priority parameter and no quota to calibrate.
 *
 * IT IS A COLLISION TEST, NOT A CAP. The retired MSS overlay capped itself at the 8 most
 * recent levels, which silently withholds events; here every SEGMENT is drawn and only the
 * redundant TEXT is dropped. Zoom in and the labels reappear, because the question is "is
 * there room", not "is this recent". Nothing to calibrate — the box is the plate's own
 * measured size.
 */
export function planLabels(items: readonly LabelBox[], rowPad: number): LabelPlan {
  const idx = items.map((_, i) => i).sort((a, b) => items[a].x1 - items[b].x1);
  const queues = {
    CHoCH: idx.filter((i) => items[i].emphasis),
    BOS: idx.filter((i) => !items[i].emphasis),
  };
  const at = { CHoCH: 0, BOS: 0 };
  const rows: { top: number; bottom: number; right: number }[] = [];
  const plan: LabelPlan = { draw: [], hidden: 0, perKind: { BOS: 0, CHoCH: 0 } };

  const fits = (it: LabelBox): boolean => {
    // Clamped to the pane's left edge: a plate at negative x is unreadable, and that was
    // one of the observed symptoms.
    const x = Math.max(0, it.x1 - it.w);
    const top = it.y - it.h - 2;
    const bottom = top + it.h;
    for (const r of rows) {
      if (top < r.bottom + rowPad && bottom > r.top - rowPad && x < r.right) return false;
    }
    rows.push({ top, bottom, right: x + it.w });
    return true;
  };

  // Whose turn it is. Flips only on a SUCCESSFUL placement (defect 3): a collision costs
  // the colliding kind its own candidate, never the other kind's turn.
  let turn: BreakKindKey = 'CHoCH';
  while (at.CHoCH < queues.CHoCH.length || at.BOS < queues.BOS.length) {
    const other: BreakKindKey = turn === 'CHoCH' ? 'BOS' : 'CHoCH';
    const kind: BreakKindKey = at[turn] < queues[turn].length ? turn : other;
    const i = queues[kind][at[kind]++];
    if (fits(items[i])) {
      plan.draw.push(i);
      plan.perKind[kind] += 1;
      turn = kind === 'CHoCH' ? 'BOS' : 'CHoCH';
    } else {
      plan.hidden += 1;
    }
  }
  // Paint order follows x so the canvas walk is left to right; selection is unchanged.
  plan.draw.sort((a, b) => items[a].x1 - items[b].x1);
  return plan;
}

export class BreakPrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApi | null = null;
  private _series: ISeriesApi<SeriesType, Time> | null = null;
  private _requestUpdate: (() => void) | null = null;
  private _data: BreakSegment[] = [];
  private _zigzag: Zigzag | null = null;
  /** The live replacement for the zigzag's last vertex. See `setZigzagTip`. */
  private _tip: ZigzagVertex | null = null;
  private _hidden = 0;
  private _drawn = { BOS: 0, CHoCH: 0 };
  private readonly _views: ISeriesPrimitivePaneView[];

  constructor() {
    this._views = [
      {
        zOrder: () => 'top',
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: (t) => this._draw(t as unknown as Target),
        }),
      },
    ];
  }

  attached(param: SeriesAttachedParameter<Time, SeriesType>): void {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews(): readonly ISeriesPrimitivePaneView[] {
    return this._views;
  }

  /** Replace the geometry and repaint. `[]` clears the overlay. */
  setData(d: BreakSegment[]): void {
    this._data = d;
    this._requestUpdate?.();
  }

  /**
   * The swing zigzag. `null` clears it.
   *
   * ON THIS PRIMITIVE RATHER THAN A NEW ONE, and that is the card's own instruction: the
   * time->x / price->y conversion, the bitmap-ratio scaling and the off-scale abort are
   * already correct here, and a fresh primitive relearns all three (the break labels
   * shipped broken three times).
   *
   * WHAT MAKES THE SEGMENT AND ITS RAY READ AS ONE OBJECT IS NOT A SHARED LITERAL, and this
   * docstring used to say it was. The open leg's period is `zigzagDash`, scaled to its own
   * segment since card `t_fce34e7f`, while the break segments keep a fixed `[5 * hr, 4 * hr]`
   * and `RangePrimitive._drawRays` has always used `[4 * hr, 4 * hr]` — three values, never
   * one. The shared price, the shared hollow tip and the shared dashed-ness are what carry it.
   */
  setZigzag(z: Zigzag | null): void {
    this._zigzag = z;
    this._tip = null;
    this._requestUpdate?.();
  }

  /**
   * Replace the open leg's FINAL vertex with the live one, and repaint. `null` restores the
   * closed-bar vertex the payload supplied.
   *
   * ITS OWN SLOT, for the same measured
   * reason (t_420df4d4 D1, restated as this card's own instruction): the live loop runs on
   * every candle write while `setZigzag` is written only by the detector refresh, which fires
   * on ROLLOVER. Without this the dashed tail's far endpoint reached the last CLOSED extreme
   * and the candles beside it had already passed it. THE MAGNITUDE LIVES IN EXACTLY ONE PLACE,
   * `overlays.zigzagLiveTip`'s docstring, which cites the probe that produced it — a second
   * copy here carried the review's superseded 5-bar-fractal figure for a whole round, which is
   * .hermes.md §6a's "two files asserting one literal is how one goes stale".
   *
   * A tip write cannot erase the polyline or the break segments, which is the whole point of
   * it being a slot rather than a `setZigzag` rebuild from the live path.
   */
  setZigzagTip(v: ZigzagVertex | null): void {
    this._tip = v;
    this._requestUpdate?.();
  }

  /**
   * The vertices this primitive would DRAW right now — the payload's, with the live tip
   * substituted for the last one when there is one.
   *
   * Public because that substitution is the whole subject of the live-tip fix and a test that
   * recomputed it from the inputs could not witness a stale draw (the finding the deleted week
   * box's `__ictWeekly.box` hook produced: a reader that recomputes cannot see what is held).
   */
  zigzagPoints(): ZigzagVertex[] {
    const pts = this._zigzag?.points ?? [];
    if (!this._tip || pts.length === 0) return pts;
    return [...pts.slice(0, -1), this._tip];
  }

  /** The zigzag as the DETECTOR supplied it, for the live path to re-derive the tip from. */
  get zigzag(): Zigzag | null {
    return this._zigzag;
  }

  /**
   * Labels suppressed at the CURRENT zoom because they had no room. Read after a paint.
   *
   * Surfaced rather than swallowed: a chart that quietly drops 189 of 208 labels is
   * indistinguishable from a detector that found 19 breaks, and "the detector found
   * nothing" is the one answer this app must not fake. `main.ts` puts this in the sidebar.
   */
  get hiddenLabels(): number {
    return this._hidden;
  }

  /**
   * Labels actually drawn, PER KIND. Read after a paint.
   *
   * Per kind, not a total, because a blended count is exactly what hid the starvation
   * bug: "19 labels drawn" looked like reasonable thinning while the real state was
   * `{BOS: 0, CHoCH: 19}` — 101 continuation breaks with no text at all. This repo's
   * standing lesson for bidirectional machines is to report metrics per side, since
   * unequal rates are the tell for a missing mirror branch.
   */
  get drawnLabels(): { BOS: number; CHoCH: number } {
    return { ...this._drawn };
  }

  private _x(time: Time, hr: number): number | null {
    const x = this._chart?.timeScale().timeToCoordinate(time);
    return x == null ? null : x * hr;
  }

  private _y(price: number, vr: number): number | null {
    const y = this._series?.priceToCoordinate(price);
    return y == null ? null : y * vr;
  }

  private _draw(target: Target): void {
    this._hidden = 0;
    this._drawn = { BOS: 0, CHoCH: 0 };
    if (!this._series) return;
    if (!this._data.length && !this._zigzag?.points.length) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr,
                                      verticalPixelRatio: vr, bitmapSize }) => {
      this._drawZigzag(ctx, hr, vr);
      if (!this._data.length) return;
      // Pass 1: every segment. Lines are cheap and they are the GEOMETRY — nothing is
      // ever withheld from the chart, however dense it gets.
      const placed: { x0: number; x1: number; y: number; s: BreakSegment }[] = [];
      for (const s of this._data) {
        const y = this._y(s.price, vr);
        const x0 = this._x(s.fromTime, hr);
        const x1 = this._x(s.toTime, hr);
        // An off-scale coordinate ABORTS the shape rather than defaulting to 0: a 0
        // would draw the segment from the pane's left edge, i.e. re-introduce the
        // full-width lie this primitive exists to avoid.
        if (y === null || x0 === null || x1 === null) continue;
        // Clamped, not dropped, when scrolled partly out of view — a level that
        // applies to visible bars must stay visible, truncated at the edge.
        const a = Math.max(0, Math.min(x0, x1));
        const b = Math.min(bitmapSize.width, Math.max(x0, x1) + OVERSHOOT_PX * hr);
        if (b <= a) continue;

        ctx.save();
        ctx.strokeStyle = s.color;
        ctx.lineWidth = Math.max(1, Math.round((s.emphasis ? 1.6 : 1) * hr));
        ctx.setLineDash([5 * hr, 4 * hr]);
        ctx.beginPath();
        ctx.moveTo(a, y);
        ctx.lineTo(b, y);
        ctx.stroke();
        ctx.restore();
        placed.push({ x0: a, x1: b, y, s });
      }

      // Pass 2: labels, only where the text PHYSICALLY FITS. The DECISION lives in
      // `planLabels` (pure, unit-tested, carries the measurements); this loop only
      // measures text widths and paints what the plan selected. Splitting them is
      // deliberate — both defects that landed here were behaviour, and behaviour that
      // lives inside a canvas callback is unreachable from any test.
      const boxes: LabelBox[] = placed.map((p) => {
        ctx.font = this._font(p.s.emphasis, vr);
        return {
          x1: p.x1,
          y: p.y,
          w: ctx.measureText(p.s.label).width + 2 * 3 * hr,
          h: 13 * vr,
          emphasis: p.s.emphasis,
        };
      });
      const plan = planLabels(boxes, ROW_PX * vr);
      this._hidden = plan.hidden;
      this._drawn = plan.perKind;
      for (const i of plan.draw) this._paintLabel(ctx, placed[i].s, boxes[i], hr, vr);
    });
  }

  /** The plate's font. One definition, so measurement and painting cannot disagree. */
  private _font(emphasis: boolean, vr: number): string {
    return `${emphasis ? 'bold ' : ''}${Math.round(10 * vr)}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  }

  /**
   * The swing zigzag: one polyline over the swing points, plus one mark per vertex.
   *
   * ONE TONE, ONE WIDTH for the whole line (`ZIGZAG_COLOR` / `ZIGZAG_WIDTH`), and exactly
   * ONE line-level dimension — the FINAL segment is dashed when it is the open leg. Degree
   * and settled-ness are vertex facts and are drawn as vertex marks here, deliberately not
   * as `SeriesMarker`s: on that layer `calculateShapeHeight` pins at 10px at every shipped
   * pane geometry, so size-by-degree quantises to one circle, and `SeriesMarker` has no
   * fill/stroke field at all, so "hollow" is inexpressible. Both are one line each on a
   * canvas. Full reasoning in the ZIGZAG DECISION block on `overlays.swingMarkers`.
   *
   * A HOLE BREAKS THE PATH rather than being bridged. An unconvertible coordinate is the
   * same hazard as in the segment pass: joining across it would draw a chord over price
   * regions the detector said nothing about — exactly the visual lie the bounded segments
   * exist to avoid.
   *
   * NOT EVERY VERTEX GETS A MARK, and the decision is `planVertexMarks`' (see its block for
   * the measurement). The POLYLINE is always complete — every convertible vertex is joined at
   * its own price — but a settled MINOR mark whose disc would land on top of an already-drawn
   * one is withheld, because at 15m density a major's own silhouette was not its own (29 of 36
   * majors had a neighbour's centre inside their footprint) and that silhouette IS the degree
   * channel. Majors and hollow marks are never withheld.
   */
  private _drawZigzag(ctx: CanvasRenderingContext2D, hr: number, vr: number): void {
    const z = this._zigzag;
    if (!z || z.points.length < 1) return;
    const pts = this.zigzagPoints().map((p) => {
      const x = this._x(p.time, hr);
      const y = this._y(p.price, vr);
      return x === null || y === null ? null : { x, y, v: p };
    });

    ctx.save();
    ctx.strokeStyle = z.color;
    ctx.lineWidth = Math.max(1, Math.round(z.width * hr));
    for (let i = 1; i < pts.length; i += 1) {
      const a = pts[i - 1];
      const b = pts[i];
      if (a === null || b === null) continue;
      // The open leg's dash, SCALED TO THIS SEGMENT so it can show at least two periods —
      // `zigzagDash` carries the measurement (28 of 57 1W legs were under ONE period of the
      // old fixed 9px literal and rendered as solid stubs). The length is measured in CSS px,
      // which is the unit the period is expressed in: `a` and `b` are bitmap px and the two
      // axes carry DIFFERENT ratios (`hr` / `vr`), so each component is un-scaled by its own
      // one. Dividing the bitmap hypotenuse by `hr` alone is wrong whenever they differ.
      const dashLast = z.dashLast && i === pts.length - 1;
      ctx.setLineDash(
        dashLast
          ? zigzagDash(Math.hypot((b.x - a.x) / hr, (b.y - a.y) / vr), hr)
          : [],
      );
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }

    // Vertices last, so a mark is never half-covered by the line that arrives at it. WHICH
    // marks have room is decided by `planVertexMarks` (pure, unit-tested, carries the
    // measurements); this loop only paints what the plan selected. The POLYLINE above is
    // already fully drawn — nothing is withheld from the geometry.
    ctx.setLineDash([]);
    ctx.fillStyle = z.color;
    const marks: VertexMark[] = [];
    for (const p of pts) {
      if (p === null) continue;
      marks.push({
        x: p.x,
        y: p.y,
        r: (p.v.major ? ZZ_R_MAJOR : ZZ_R_MINOR) * hr,
        major: p.v.major,
        pending: p.v.pending,
      });
    }
    for (const mi of planVertexMarks(marks)) {
      const m = marks[mi];
      ctx.beginPath();
      ctx.arc(m.x, m.y, m.r, 0, 2 * Math.PI);
      // Hollow = printed but not settled. Filled = settled.
      if (m.pending) ctx.stroke();
      else ctx.fill();
    }
    ctx.restore();
  }

  /**
   * Paint one label plate. The name, above the line, at the BREAK end.
   *
   * At the break end (not the anchor end) because that is the instant the label is
   * about, and because it is what the reference drawing does — the eye follows price
   * into the break and reads the name where it arrives.
   *
   * A colliding plate is DROPPED by `planLabels` rather than stepped aside (which is what
   * `levelplate.drawPlate` does for account levels): those plates are all pinned to the
   * left edge and their x is free, but this one names a specific break, so moving it would
   * attach the word to the wrong segment. A missing label is honest; a misplaced one lies.
   */
  private _paintLabel(
    ctx: CanvasRenderingContext2D,
    s: BreakSegment,
    box: LabelBox,
    hr: number,
    vr: number,
  ): void {
    ctx.save();
    ctx.font = this._font(s.emphasis, vr);
    const x = Math.max(0, box.x1 - box.w);
    const top = box.y - box.h - 2;
    ctx.fillStyle = 'rgba(19, 23, 34, 0.78)';
    ctx.fillRect(x, top, box.w, box.h);
    ctx.fillStyle = s.color;
    ctx.textBaseline = 'middle';
    ctx.fillText(s.label, x + 3 * hr, top + box.h / 2);
    ctx.restore();
  }
}
